import {defineSlotRecipe} from '@chakra-ui/react3'

const LINK_CHEVRON_ACTIVE = {
    opacity: {s: 1},
    transform: {s: 'translateX(4px)'}
}

const CARD_CHEVRON_ACTIVE = {
    transform: 'translateX(8px)'
}

export const navigationParentRecipe = defineSlotRecipe({
    // Slots styled in base: root, chevron
    // Shared slots: title
    // Card-variant-specific: imageContainer, content, copy, price
    slots: ['root', 'title', 'chevron', 'imageContainer', 'content', 'copy', 'price'],
    base: {
        root: {
            display: 'flex',
            alignItems: 'center',
            width: 'full',
            cursor: 'pointer',
            border: 'none',
            background: 'none',
            textAlign: 'start',
            padding: 0,
            _focusVisible: {
                outlineOffset: '1px',
                outlineStyle: 'solid',
                outlineWidth: '1px',
                outlineColor: 'border.strong',
                borderRadius: 's'
            }
        },
        chevron: {
            flexShrink: 0,
            color: 'icons.primary',
            _icon: {
                // Without explicit size the chakra('svg') wrapper emits no class
                // and the browser/reset collapses the SVG to zero dimensions.
                // 1.5rem = boxSize="6" (the design-system standard icon size).
                width: '1.5rem',
                height: '1.5rem'
            }
        }
    },
    variants: {
        variant: {
            // ─── Link variant ───────────────────────────────────────────────
            // Root is the full-width flex row (title left · chevron right).
            // `title` slot holds the navigation label typography.
            link: {
                root: {
                    justifyContent: 'space-between',
                    paddingBlock: 'spacing-4',
                    alignSelf: 'stretch',
                    color: {base: 'text.primary', s: 'text.tertiary'},
                    transition: 'color 0.5s cubic-bezier(0.5, 0, 0, 1)',
                    _hover: {
                        color: 'text.primary'
                    },
                    _expanded: {
                        color: 'text.primary'
                    },
                    _active: {
                        color: 'text.primary'
                    }
                },
                title: {
                    textStyle: 'heading.medium',
                    alignSelf: 'stretch',
                    color: 'inherit',
                    transition: 'color 0.5s cubic-bezier(0.5, 0, 0, 1)',
                    lineClamp: 2
                },
                chevron: {
                    color: 'icons.primary',
                    opacity: {base: 1, s: 0},
                    transform: 'translateX(0)',
                    transition:
                        'opacity 0.1s cubic-bezier(0.5, 0, 0, 1), transform 0.5s cubic-bezier(0.5, 0, 0, 1)',
                    _groupHover: LINK_CHEVRON_ACTIVE,
                    _groupFocus: LINK_CHEVRON_ACTIVE,
                    _groupExpanded: LINK_CHEVRON_ACTIVE,
                    _groupActive: LINK_CHEVRON_ACTIVE
                }
            },
            // ─── Card variant ────────────────────────────────────────────────
            // Root is a row card: imageContainer · content column (title + copy + price).
            // `title`, `imageContainer`, `content`, `copy`, `price`
            // are card-specific and defined here only.
            card: {
                root: {
                    flexDirection: 'row',
                    gap: 'spacing-12',
                    paddingX: 'spacing-12',
                    paddingY: 'spacing-6',
                    borderRadius: 's',
                    backgroundColor: 'background.section.subtle',
                    border: '1px solid',
                    borderColor: 'transparent',
                    transition:
                        'background-color 0.2s ease, border-color 0.2s ease, border-radius 0.2s ease',
                    _hover: {
                        backgroundColor: 'background.section.default'
                    },
                    _expanded: {
                        backgroundColor: 'background.section.default'
                    },
                    _active: {
                        backgroundColor: 'background.section.default'
                    },
                    _focus: {
                        outline: 'none',
                        backgroundColor: 'background.section.default'
                    },
                    _focusVisible: {
                        outline: 'none',
                        backgroundColor: 'background.section.default',
                        borderRadius: '0.625rem',
                        borderColor: 'border.strong'
                    }
                },
                title: {
                    textStyle: 'body.small',
                    color: 'text.primary',
                    alignSelf: 'stretch',
                    lineClamp: 2
                },
                chevron: {
                    opacity: 1,
                    transform: 'translateX(0)',
                    transition: 'transform 0.3s cubic-bezier(0.5, 0, 0, 1)',
                    _groupHover: CARD_CHEVRON_ACTIVE,
                    _groupFocus: CARD_CHEVRON_ACTIVE,
                    _groupExpanded: CARD_CHEVRON_ACTIVE,
                    _groupActive: CARD_CHEVRON_ACTIVE
                },
                imageContainer: {
                    display: 'flex',
                    width: '3.75rem',
                    height: '3.75rem',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexShrink: 0,
                    aspectRatio: '1/1',
                    borderRadius: 's', // --Radius-S = 0.5rem
                    overflow: 'hidden',
                    backgroundColor: 'background.section.subtle',
                    transition: 'background-color 0.2s ease',
                    _groupHover: {
                        backgroundColor: 'background.section.default'
                    },
                    _groupFocus: {
                        backgroundColor: 'background.section.default'
                    },
                    _groupExpanded: {
                        backgroundColor: 'background.section.default'
                    },
                    _groupActive: {
                        backgroundColor: 'background.section.default'
                    }
                },
                content: {
                    flex: 1,
                    minWidth: 0 // allows text children to truncate inside flex
                },
                copy: {
                    textStyle: 'body.xsmall',
                    color: 'text.secondary',
                    lineClamp: 2,
                    alignSelf: 'stretch'
                },
                price: {
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'baseline',
                    gap: '0',
                    paddingTop: 'spacing-4',
                    textStyle: 'body.xsmall',
                    color: 'text.secondary'
                }
            }
        },
        // The CMS accent flag, taken as authored. The schema types it as the
        // string form of a boolean while the JSON template emits it unquoted, so
        // both shapes reach the recipe. The styling lives in the compound variant
        // below, which the flag pairs with the variant it applies to; the axis is
        // declared here to keep `accentColor` a recipe prop, so it is split off
        // the props rather than reaching the DOM.
        accentColor: {
            false: {},
            true: {}
        }
    },
    compoundVariants: [
        // Accent colour applies to the `link` variant alone. Compound variants
        // match by strict equality, so both authored shapes of the flag are
        // listed.
        {
            variant: 'link',
            accentColor: [true, 'true'],
            css: {
                title: {
                    color: 'text.brand-default'
                },
                chevron: {color: 'text.brand-default'}
            }
        }
    ],
    defaultVariants: {
        variant: 'link',
        accentColor: false
    }
})
